import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n-context";
import { useAuth } from "@/lib/auth-context";
import { Mic, UtensilsCrossed, Store, Handshake, Crown, Trophy, Award, ArrowRight, Lock, ShieldCheck } from "lucide-react";

const CATS = [
  { key: "performer",         icon: Mic,            to: "/register/performer",         color: "bg-brand-indigo" },
  { key: "restaurant",        icon: UtensilsCrossed, to: "/register/restaurant",       color: "bg-[#E65100]" },
  { key: "trader",            icon: Store,           to: "/register/trader",           color: "bg-[#8D6E63]" },
  { key: "business_matching", icon: Handshake,       to: "/register/business_matching", color: "bg-[#0277BD]" },
  { key: "sponsor",           icon: Crown,           to: "/register/sponsor",           color: "bg-brand-red" },
];

const INFO_ONLY = [
  { key: "speech", icon: Trophy, label_key: "reg_speech", to: "/info/speech-contest" },
  { key: "stamp",  icon: Award,  label_key: "reg_stamp",  to: "/info/stamp-rally" },
];

const LABEL_MAP = {
  performer: "reg_performer",
  restaurant: "reg_restaurant",
  trader: "reg_trader",
  business_matching: "reg_bm",
  sponsor: "reg_sponsor",
};

export default function RegisterHubPage() {
  const { t } = useLang();
  const { user, login } = useAuth();
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
      <div className="mb-12 max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-brand-red font-semibold mb-3">Participate</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold">{t("register_title")}</h1>
        <p className="text-muted-foreground mt-4 text-lg">{t("deadline")} · {t("privacy_note")}</p>
      </div>

      {!user && (
        <div className="mb-8 bg-brand-canvas border border-border rounded-2xl p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-brand-red" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg leading-tight">
              Browse all categories freely — sign in only when you&apos;re ready to submit.
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              We use Google Sign-in so your data stays private. Only you and the organizer can see it.
            </p>
          </div>
          <button
            onClick={login}
            className="px-5 h-10 rounded-full bg-brand-red hover:bg-brand-red-dark text-white text-sm font-semibold whitespace-nowrap"
            data-testid="hub-signin-btn"
          >
            {t("login")}
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {CATS.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.key}
              to={c.to}
              className="group relative overflow-hidden rounded-3xl bg-white border border-border p-7 hover:-translate-y-1 transition-transform"
              data-testid={`reg-card-${c.key}`}
            >
              <div className={`w-12 h-12 rounded-full ${c.color} flex items-center justify-center text-white`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="mt-5">
                <div className="font-display text-2xl font-bold leading-tight">{t(LABEL_MAP[c.key])}</div>
                <div className="mt-3 text-sm flex items-center gap-2 text-brand-red font-medium">
                  Open form <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
              <div className="absolute top-4 right-4 text-muted-foreground text-xs flex items-center gap-1">
                <Lock className="w-3 h-3" /> Private
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-14 mb-4">
        <h2 className="font-display text-2xl font-bold">Also happening at IJFF 2026</h2>
        <p className="text-sm text-muted-foreground mt-1">Confirmed programs. Registration details will be published soon.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {INFO_ONLY.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.key}
              to={c.to}
              className="group relative overflow-hidden rounded-3xl bg-brand-canvas p-7 hover:-translate-y-1 transition-transform"
              data-testid={`info-card-${c.key}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-gold flex items-center justify-center text-black">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display text-2xl font-bold leading-tight">{t(c.label_key)}</div>
                  <div className="text-xs uppercase tracking-widest text-brand-red font-semibold mt-1">
                    Confirmed program
                  </div>
                </div>
              </div>
              <div className="mt-4 text-sm flex items-center gap-2 text-foreground/70 font-medium">
                Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
